import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  chartBaseTooltip,
  chartIntegerTicks,
  chartLegend,
} from './chartTokens';
import {
  IPeriodCompareInput,
  periodCompareData,
  periodCompareDeltaPct,
  periodCompareLengthsDiffer,
  periodCompareStackTotal,
} from './periodCompareData';
import { registerCharts } from './registerCharts';

registerCharts();

const DASH = '—';

export interface IPeriodCompareChartProps extends IPeriodCompareInput {
  /** Формат значений и левой оси. */
  format: (value: number) => string;
  /** Формат линии правой оси. */
  lineFormat?: (value: number) => string;
  /** Дельта в подсказке: «+33 %»; `null` — не показывать. */
  deltaLabel?: (pct: number) => string;
  /**
   * Повторное ревью 0103 (A3): корзины разной длины — вместо процента
   * «разная длина: 4 и 6 дн.»; не задано — без строки.
   */
  lengthsLabel?: (days: number, compareDays: number) => string;
  /** Приписка к заголовку неполной корзины: «неполная неделя». */
  partialNote?: string;
  /** Клик по корзине — номер корзины. */
  onSelectBucket?: (index: number) => void;
  /** Подпись для чтеца экрана: что на графике. */
  ariaLabel?: string;
  /** Счётчик на левой оси: только целые отметки. */
  integer?: boolean;
  /** Счётчик на правой оси (линия): только целые отметки. */
  lineInteger?: boolean;
}

/**
 * ТЗ 0103 — главный график «период против периода»: текущие столбики (можно
 * стеком) + сравнение столбиками того же цвета на 35 % или пунктиром, линия
 * на правой оси по желанию. В подсказке — оба реальных диапазона корзины:
 * «Пн 14.09: 12 · Пн 07.09: 9 (+33 %)». Клик по корзине — `onSelectBucket`.
 */
export const PeriodCompareChart = memo(
  ({
    format,
    lineFormat,
    deltaLabel,
    lengthsLabel,
    partialNote,
    onSelectBucket,
    ariaLabel,
    integer = false,
    lineInteger = false,
    ...input
  }: IPeriodCompareChartProps) => {
    const {
      buckets,
      currentLabel,
      compareLabel,
      color,
      compareStyle,
      stackSeries,
      line,
    } = input;
    const built = useMemo(
      () =>
        periodCompareData({
          buckets,
          currentLabel,
          compareLabel,
          color,
          compareStyle,
          stackSeries,
          line,
        }),
      [
        buckets,
        currentLabel,
        compareLabel,
        color,
        compareStyle,
        stackSeries,
        line,
      ],
    );

    const options: ChartOptions<'bar'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        interaction: { mode: 'index', intersect: false },
        onClick: (_event, elements) => {
          const [first] = elements;
          if (first && onSelectBucket) onSelectBucket(first.index);
        },
        onHover: (event, elements) => {
          const target = event.native?.target as HTMLElement | undefined;
          if (target && onSelectBucket) {
            target.style.cursor = elements.length ? 'pointer' : 'default';
          }
        },
        plugins: {
          legend: chartLegend,
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              title: (items) => {
                const [first] = items;
                if (!first) return '';
                const bucket = buckets[first.dataIndex];
                if (!bucket) return '';
                return bucket.partial && partialNote
                  ? `${bucket.title} · ${partialNote}`
                  : bucket.title;
              },
              label: (item) => {
                const dataset = built.datasets[item.datasetIndex];
                const raw = item.raw as number | null;
                const valueFormat =
                  dataset?.role === 'line' && lineFormat ? lineFormat : format;
                const formatted =
                  raw === null ? DASH : valueFormat(Number(raw));
                const bucket = buckets[item.dataIndex];
                if (dataset?.role === 'compare' && bucket?.compareTitle) {
                  const title =
                    bucket.comparePartial && partialNote
                      ? `${bucket.compareTitle} · ${partialNote}`
                      : bucket.compareTitle;
                  return `${title}: ${formatted}`;
                }
                return `${item.dataset.label}: ${formatted}`;
              },
              footer: (items) => {
                const [first] = items;
                if (!first || !deltaLabel || !built.hasCompare) return '';
                const bucket = buckets[first.dataIndex];
                if (!bucket) return '';
                if (periodCompareLengthsDiffer(bucket)) {
                  return lengthsLabel
                    ? lengthsLabel(
                        bucket.days as number,
                        bucket.compareDays as number,
                      )
                    : '';
                }
                const current = stackSeries?.length
                  ? periodCompareStackTotal(bucket.stack)
                  : bucket.current;
                const pct = periodCompareDeltaPct(current, bucket.previous);
                return pct === null ? '' : deltaLabel(pct);
              },
            },
          },
        },
        scales: {
          x: {
            stacked: Boolean(stackSeries?.length),
            grid: { display: false },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 16,
              font: CHART_TICK_FONT,
              color: CHART_INK,
            },
          },
          y: {
            stacked: Boolean(stackSeries?.length),
            beginAtZero: true,
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              ...chartIntegerTicks(integer),
              callback: (value) => format(Number(value)),
            },
          },
          ...(built.rightAxis
            ? {
                y1: {
                  position: 'right' as const,
                  beginAtZero: true,
                  grid: { drawOnChartArea: false },
                  ticks: {
                    font: CHART_TICK_FONT,
                    color: CHART_INK,
                    ...chartIntegerTicks(lineInteger),
                    callback: (value: string | number) =>
                      (lineFormat ?? format)(Number(value)),
                  },
                },
              }
            : {}),
        },
      }),
      [
        built,
        buckets,
        format,
        lineFormat,
        deltaLabel,
        lengthsLabel,
        partialNote,
        onSelectBucket,
        stackSeries,
        integer,
        lineInteger,
      ],
    );

    // `role` в рядах chart.js не читает — он нужен подсказке.
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart
          type="bar"
          data={{ labels: built.labels, datasets: built.datasets } as never}
          options={options as never}
        />
      </C.ChartBox>
    );
  },
);

PeriodCompareChart.displayName = 'PeriodCompareChart';
